import DOMPurify from 'dompurify';

/**
 * Custom DOMPurify configuration designed specifically for Spark's
 * TinyMCE WYSIWYG outputs, proposal sections, and contract revision annotations.
 * 
 * Preserves:
 *  - Inline styles (colors, font-sizes, margins, table layout widths)
 *  - CSS classes (including .strike-redline, .comment-highlight, .strike-comment-highlight)
 *  - Custom data attributes (data-section-type, data-revision-id, data-variable)
 *  - Table layouts (colspan, rowspan, border, cellpadding, cellspacing)
 *  - Links, images, lists, and semantic tags
 * 
 * Blocks:
 *  - <script>, <iframe>, <object>, <embed>, <form>
 *  - Inline event handlers (onerror, onclick, onload, onmouseover, etc.)
 *  - javascript: pseudo-protocol URIs
 */
const WYSIWYG_PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'col', 'colgroup',
    'dd', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'footer', 'h1', 'h2',
    'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'i', 'img', 'li', 'main', 'mark',
    'nav', 'ol', 'p', 'pre', 'q', 's', 'section', 'small', 'span', 'strike',
    'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
    'u', 'ul', 'style', 'svg', 'path'
  ],
  ALLOWED_ATTR: [
    'align', 'alt', 'aria-describedby', 'aria-hidden', 'aria-label', 'aria-labelledby',
    'border', 'cellpadding', 'cellspacing', 'class', 'color', 'colspan', 'data-*',
    'dir', 'height', 'href', 'id', 'loading', 'rel', 'role', 'rowspan', 'scope',
    'src', 'style', 'tabindex', 'target', 'title', 'type', 'valign', 'width',
    'data-section-type', 'data-revision-id', 'data-action', 'data-variable'
  ],
  ALLOW_DATA_ATTR: true,
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'applet', 'form', 'input', 'textarea', 'button'],
  FORBID_ATTR: [
    'onerror', 'onload', 'onclick', 'ondblclick', 'onmousedown', 'onmouseup',
    'onmouseover', 'onmousemove', 'onmouseout', 'onmouseenter', 'onmouseleave',
    'onkeydown', 'onkeypress', 'onkeyup', 'onsubmit', 'onreset', 'onselect',
    'onchange', 'onfocus', 'onblur', 'oninput', 'oncontextmenu', 'formaction'
  ],
  FORCE_BODY: false,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false
};

/**
 * Sanitizes rich HTML content while strictly preserving WYSIWYG formatting,
 * CSS styles, and contract revision annotations.
 * 
 * @param {string} html Raw HTML string from database or user input
 * @returns {string} Clean, safe HTML string
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') {
    return html || '';
  }

  // Ensure DOMPurify is executed in browser window context
  if (typeof window !== 'undefined' && DOMPurify.sanitize) {
    return DOMPurify.sanitize(html, WYSIWYG_PURIFY_CONFIG);
  }

  return html;
}

export default sanitizeHtml;
